import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const navItems = [
    { label: "Home", anchor: "#home" },
    { label: "The Experience", anchor: "#experience" },
    { label: "Cities", anchor: "#cities" },
    { label: "Upgrade", anchor: "#upgrade" },
    { label: "The Code", anchor: "#the-code" },
    { label: "Specials", anchor: "#specials" },
    { label: "Contact", href: "/contact" },
  ];

  const handleNavClick = (item, e) => {
    if (item.href) {
      // External page link
      return;
    }
    
    e.preventDefault();
    setMobileMenuOpen(false);
    
    // If not on home page, navigate there first
    if (location.pathname !== "/") {
      navigate("/");
      // Wait for navigation then scroll
      setTimeout(() => {
        const element = document.querySelector(item.anchor);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } else {
      const element = document.querySelector(item.anchor);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <>
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled 
            ? 'bg-[#0B0B0F]/90 backdrop-blur-md shadow-lg' 
            : 'bg-[#0B0B0F]/70'
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <a 
              href="/" 
              onClick={handleLogoClick}
              className="font-heading font-bold text-lg md:text-xl tracking-tight"
              data-testid="nav-logo"
            >
              <span style={{ color: "#F4F1E8" }}>CINCO </span>
              <span className="text-terracotta">DE</span>
              <span style={{ color: "#F4F1E8" }}> CRAWL</span>
            </a>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              {navItems.map((item) => (
                item.href ? (
                  <Link
                    key={item.label}
                    to={item.href}
                    className="nav-link font-accent text-sm uppercase tracking-wider text-white/70 hover:text-terracotta transition-colors relative group"
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {item.label}
                    <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-[#D4AF37] group-hover:w-full transition-all duration-300" />
                  </Link>
                ) : (
                  <a
                    key={item.label}
                    href={item.anchor}
                    onClick={(e) => handleNavClick(item, e)}
                    className="nav-link font-accent text-sm uppercase tracking-wider text-white/70 hover:text-terracotta transition-colors relative group"
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {item.label}
                    <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-[#D4AF37] group-hover:w-full transition-all duration-300" />
                  </a>
                )
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-white/70 hover:text-white transition-colors"
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div 
        className={`fixed inset-0 z-40 md:hidden transition-all duration-300 ${
          mobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-[#0B0B0F]/95 backdrop-blur-lg"
          onClick={() => setMobileMenuOpen(false)}
        />
        
        {/* Menu Content */}
        <div className="relative flex flex-col items-center justify-center h-full">
          <div className="flex flex-col items-center gap-8">
            {navItems.map((item, i) => (
              item.href ? (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="font-heading font-bold text-3xl uppercase tracking-tight text-white/80 hover:text-terracotta transition-colors"
                  style={{ 
                    animationDelay: `${i * 0.05}s`,
                    animation: mobileMenuOpen ? 'fadeInUp 0.4s ease forwards' : 'none',
                    opacity: 0,
                  }}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.label}
                  href={item.anchor}
                  onClick={(e) => handleNavClick(item, e)}
                  className="font-heading font-bold text-3xl uppercase tracking-tight text-white/80 hover:text-terracotta transition-colors"
                  style={{ 
                    animationDelay: `${i * 0.05}s`,
                    animation: mobileMenuOpen ? 'fadeInUp 0.4s ease forwards' : 'none',
                    opacity: 0,
                  }}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  {item.label}
                </a>
              )
            ))}
          </div>
          
          {/* Close hint */}
          <p className="absolute bottom-10 text-white/30 text-sm font-accent uppercase tracking-wider">
            Tap to close
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default Navbar;
